// src/pages/AccountSettings.jsx
import React, { useState, useEffect } from "react";
import { useNavigate }                from "react-router-dom";
import { useTranslation }             from "react-i18next";
import { updateProfile, sendPasswordResetEmail } from "firebase/auth";
import { ref, update }                from "firebase/database";
import { auth, db }                   from "../firebase/config";
import { useAuth, isDisplayNameAvailable } from "../context/AuthContext";
import { syncPublicProfile }           from "../firebase/auth";
import i18n                           from "../i18n";

// ─── Renk sabitleri ───────────────────────────────────────────────────────────
const C = {
  bg:         "#0d0a05",
  card:       "#12100a",
  border:     "rgba(212,168,83,0.15)",
  borderHov:  "rgba(212,168,83,0.35)",
  gold:       "#d4a853",
  goldDim:    "rgba(212,168,83,0.5)",
  goldFaint:  "rgba(212,168,83,0.08)",
  ivory:      "#e8d5b0",
  ivoryDim:   "rgba(232,213,176,0.55)",
  ivoryGhost: "rgba(232,213,176,0.25)",
  red:        "#e05c5c",
  redDim:     "rgba(224,92,92,0.12)",
  redBorder:  "rgba(224,92,92,0.35)",
  green:      "#6ec97a",
};

// ─── Avatar listesi (profilfoto1.png … profilfoto72.png) ─────────────────────
// Görseller public/profilfoto/ klasöründe — hash'lenmez, sabit URL kalır.
// src/assets/ altındaki dosyalar build'de hash alır; public/ almaz.
const AVATAR_COUNT = 72;
const avatarList = Array.from(
  { length: AVATAR_COUNT },
  (_, i) => `/profilfoto/profilfoto${i + 1}.png`
);

// ─── Ülkeler ──────────────────────────────────────────────────────────────────
const COUNTRIES = [
  "Afghanistan","Albania","Algeria","Andorra","Angola","Antigua and Barbuda",
  "Argentina","Armenia","Australia","Austria","Azerbaijan","Bahamas","Bahrain",
  "Bangladesh","Barbados","Belarus","Belgium","Belize","Benin","Bhutan","Bolivia",
  "Bosnia and Herzegovina","Botswana","Brazil","Brunei","Bulgaria","Burkina Faso",
  "Burundi","Cabo Verde","Cambodia","Cameroon","Canada","Central African Republic",
  "Chad","Chile","China","Colombia","Comoros","Congo","Costa Rica","Croatia","Cuba",
  "Cyprus","Czech Republic","Denmark","Djibouti","Dominica","Dominican Republic",
  "Ecuador","Egypt","El Salvador","Equatorial Guinea","Eritrea","Estonia","Eswatini",
  "Ethiopia","Fiji","Finland","France","Gabon","Gambia","Georgia","Germany","Ghana",
  "Greece","Grenada","Guatemala","Guinea","Guinea-Bissau","Guyana","Haiti","Honduras",
  "Hungary","Iceland","India","Indonesia","Iran","Iraq","Ireland","Israel","Italy",
  "Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kiribati","Kuwait","Kyrgyzstan",
  "Laos","Latvia","Lebanon","Lesotho","Liberia","Libya","Liechtenstein","Lithuania",
  "Luxembourg","Madagascar","Malawi","Malaysia","Maldives","Mali","Malta",
  "Marshall Islands","Mauritania","Mauritius","Mexico","Micronesia","Moldova",
  "Monaco","Mongolia","Montenegro","Morocco","Mozambique","Myanmar","Namibia",
  "Nauru","Nepal","Netherlands","New Zealand","Nicaragua","Niger","Nigeria",
  "North Korea","North Macedonia","Norway","Oman","Pakistan","Palau","Palestine",
  "Panama","Papua New Guinea","Paraguay","Peru","Philippines","Poland","Portugal",
  "Qatar","Romania","Russia","Rwanda","Saint Kitts and Nevis","Saint Lucia",
  "Saint Vincent and the Grenadines","Samoa","San Marino","Saudi Arabia","Senegal",
  "Serbia","Seychelles","Sierra Leone","Singapore","Slovakia","Slovenia",
  "Solomon Islands","Somalia","South Africa","South Korea","South Sudan","Spain",
  "Sri Lanka","Sudan","Suriname","Sweden","Switzerland","Syria","Taiwan",
  "Tajikistan","Tanzania","Thailand","Timor-Leste","Togo","Tonga",
  "Trinidad and Tobago","Tunisia","Turkey","Turkmenistan","Tuvalu","Uganda",
  "Ukraine","United Arab Emirates","United Kingdom","United States","Uruguay",
  "Uzbekistan","Vanuatu","Vatican City","Venezuela","Vietnam","Yemen",
  "Zambia","Zimbabwe",
];

// ═══════════════════════════════════════════════════════════════════════════════
// KÜÇÜK UI BİLEŞENLERİ
// ═══════════════════════════════════════════════════════════════════════════════

function Toast({ msg, type }) {
  if (!msg) return null;
  const isSuccess = type !== "error";
  return (
    <div style={{
      position: "fixed", bottom: 32, right: 32, zIndex: 9999,
      background: isSuccess ? "linear-gradient(135deg, #131009 0%, #1a1208 100%)" : C.card,
      border: `1px solid ${isSuccess ? "rgba(212,168,83,0.55)" : C.redBorder}`,
      padding: "16px 24px", maxWidth: 360,
      display: "flex", alignItems: "center", gap: 12,
      boxShadow: isSuccess
        ? "0 8px 40px rgba(0,0,0,0.7), 0 0 32px rgba(212,168,83,0.12), inset 0 0 40px rgba(212,168,83,0.03)"
        : "0 8px 40px rgba(0,0,0,0.7), 0 0 20px rgba(224,92,92,0.08)",
      animation: "toastIn 0.35s cubic-bezier(0.16,1,0.3,1)",
    }}>
      {/* İkon */}
      <div style={{
        width: 28, height: 28, flexShrink: 0,
        border: `1px solid ${isSuccess ? "rgba(212,168,83,0.35)" : "rgba(224,92,92,0.35)"}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: isSuccess ? "#d4a853" : C.red,
        fontSize: 13,
        boxShadow: isSuccess ? "0 0 10px rgba(212,168,83,0.15)" : "none",
      }}>
        {isSuccess ? "⚜" : "⚠"}
      </div>
      <div>
        {isSuccess && (
          <div style={{
            fontFamily: "'Courier Prime', monospace", fontSize: 8,
            letterSpacing: "0.3em", textTransform: "uppercase",
            color: "rgba(212,168,83,0.45)", marginBottom: 3,
          }}>Başarılı</div>
        )}
        <span style={{
          fontFamily: "'Courier Prime', monospace", fontSize: 11,
          color: isSuccess ? "#e8d5b0" : C.red,
          letterSpacing: "0.04em", lineHeight: 1.5,
        }}>{msg}</span>
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 22 }}>
      <span style={{
        fontFamily: "'Playfair Display', serif", fontSize: 15,
        fontWeight: 700, color: C.gold, letterSpacing: "0.04em",
      }}>{children}</span>
    </div>
  );
}

function Label({ children }) {
  return (
    <div style={{
      fontFamily: "'Courier Prime', monospace", fontSize: 9,
      letterSpacing: "0.28em", color: C.goldDim,
      textTransform: "uppercase", marginBottom: 8,
    }}>{children}</div>
  );
}

function Input({ value, onChange, placeholder, type = "text", disabled, onKeyDown }) {
  return (
    <input
      type={type} value={value} onChange={onChange}
      placeholder={placeholder} disabled={disabled} onKeyDown={onKeyDown}
      style={{
        display: "block", width: "100%", boxSizing: "border-box",
        padding: "11px 14px", marginBottom: 14,
        borderRadius: 6,
        background: disabled ? "rgba(212,168,83,0.03)" : "#1a1208",
        border: `1px solid ${C.border}`,
        color: disabled ? C.goldDim : C.ivory,
        fontFamily: "'Courier Prime', monospace", fontSize: 13,
        outline: "none", cursor: disabled ? "not-allowed" : "text",
        transition: "border-color 0.2s",
      }}
      onFocus={e => { if (!disabled) e.target.style.borderColor = C.borderHov; }}
      onBlur={e  => { e.target.style.borderColor = C.border; }}
    />
  );
}

function TextArea({ value, onChange, placeholder, maxLength, rows = 4 }) {
  const remaining = maxLength - (value?.length || 0);
  return (
    <div style={{ position: "relative", marginBottom: 14 }}>
      <textarea
        value={value} onChange={onChange}
        placeholder={placeholder} maxLength={maxLength} rows={rows}
        style={{
          display: "block", width: "100%", boxSizing: "border-box",
          padding: "11px 14px 28px",
          borderRadius: 6,
          background: "#1a1208", border: `1px solid ${C.border}`,
          color: C.ivory, fontFamily: "'Courier Prime', monospace",
          fontSize: 13, outline: "none", resize: "vertical",
          lineHeight: 1.65, transition: "border-color 0.2s",
        }}
        onFocus={e => { e.target.style.borderColor = C.borderHov; }}
        onBlur={e  => { e.target.style.borderColor = C.border; }}
      />
      <span style={{
        position: "absolute", bottom: 8, right: 12,
        fontFamily: "'Courier Prime', monospace", fontSize: 9,
        letterSpacing: "0.12em",
        color: remaining < 30 ? C.red : "rgba(212,168,83,0.28)",
        pointerEvents: "none",
      }}>{remaining} / {maxLength}</span>
    </div>
  );
}

function Select({ value, onChange, options, placeholder }) {
  return (
    <div style={{ position: "relative", marginBottom: 14 }}>
      <select
        value={value} onChange={onChange}
        style={{
          display: "block", width: "100%", boxSizing: "border-box",
          padding: "11px 36px 11px 14px",
          borderRadius: 6,
          background: "#1a1208", border: `1px solid ${C.border}`,
          color: value ? C.ivory : C.ivoryGhost,
          fontFamily: "'Courier Prime', monospace", fontSize: 13,
          outline: "none", appearance: "none", cursor: "pointer",
          transition: "border-color 0.2s",
        }}
        onFocus={e => { e.target.style.borderColor = C.borderHov; }}
        onBlur={e  => { e.target.style.borderColor = C.border; }}
      >
        <option value="" style={{ background: "#1a1208", color: C.ivoryGhost }}>
          {placeholder}
        </option>
        {options.map(o => (
          <option key={o} value={o} style={{ background: "#1a1208", color: C.ivory }}>{o}</option>
        ))}
      </select>
      <span style={{
        position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
        color: C.goldDim, pointerEvents: "none", fontSize: 10,
      }}>▼</span>
    </div>
  );
}

function LangSwitch({ value, onChange, onImmediate }) {
  return (
    <div style={{
      display: "inline-flex", border: `1px solid ${C.border}`,
      marginBottom: 14, overflow: "hidden", borderRadius: 8,
    }}>
      {[{ id: "tr", label: "Türkçe" }, { id: "en", label: "English" }].map(({ id, label }) => {
        const active = value === id;
        return (
          <button key={id} onClick={() => { onChange(id); onImmediate?.(id); }} style={{
            padding: "9px 22px", background: active ? C.goldFaint : "transparent",
            border: "none",
            borderRight: id === "tr" ? `1px solid ${C.border}` : "none",
            color: active ? C.gold : C.ivoryGhost,
            fontFamily: "'Courier Prime', monospace", fontSize: 11,
            letterSpacing: "0.22em", textTransform: "uppercase",
            cursor: "pointer", transition: "all 0.2s",
            fontWeight: active ? "bold" : "normal",
          }}>
            {label}
          </button>
        );
      })}
    </div>
  );
}

function Btn({ onClick, children, disabled, variant = "primary" }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick} disabled={disabled}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        padding: "11px 24px", borderRadius: 8, cursor: disabled ? "not-allowed" : "pointer",
        background: variant === "primary"
          ? (disabled ? "rgba(212,168,83,0.25)" : hov ? "#c49840" : C.gold)
          : "transparent",
        border: variant === "outline"
          ? `1px solid ${hov ? C.gold : C.border}` : "none",
        color: variant === "primary" ? "#0d0a05" : (hov ? C.gold : C.goldDim),
        fontFamily: "'Courier Prime', monospace", fontWeight: "bold",
        fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
        transition: "all 0.2s ease", opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
}

function DangerBtn({ onClick, children, disabled }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick} disabled={disabled}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        padding: "11px 24px", borderRadius: 8, cursor: disabled ? "not-allowed" : "pointer",
        background: hov && !disabled ? "rgba(224,92,92,0.18)" : "transparent",
        border: `1px solid ${hov && !disabled ? C.red : C.redBorder}`,
        color: disabled ? "rgba(224,92,92,0.35)" : C.red,
        fontFamily: "'Courier Prime', monospace", fontWeight: "bold",
        fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
        transition: "all 0.2s ease", opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

function Msg({ type, text }) {
  if (!text) return null;
  return (
    <p style={{
      fontFamily: "'Courier Prime', monospace", fontSize: 11,
      color: type === "error" ? C.red : C.green,
      margin: "0 0 12px", letterSpacing: "0.05em",
    }}>
      {type === "error" ? "⚠ " : "✓ "}{text}
    </p>
  );
}

function Card({ children, style: extra = {} }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 8,
      padding: "28px 32px", marginBottom: 0,
      ...extra,
    }}>
      {children}
    </div>
  );
}

function GoldRule() {
  return (
    <div style={{
      height: 1, margin: "0 0 22px",
      background: "linear-gradient(90deg,transparent,rgba(212,168,83,0.12),transparent)",
    }} />
  );
}

// ── Ayarları Mühürle — imzalı kaydırma butonu ────────────────────────────────
function SealBtn({ onClick, children, disabled }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick} disabled={disabled}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        position: "relative", overflow: "hidden",
        padding: "13px 32px", borderRadius: 8,
        background: disabled
          ? "rgba(212,168,83,0.12)"
          : hov
          ? "rgba(212,168,83,0.14)"
          : "rgba(212,168,83,0.08)",
        border: `1px solid ${disabled ? "rgba(212,168,83,0.2)" : hov ? "#d4a853" : "rgba(212,168,83,0.4)"}`,
        color: disabled ? "rgba(212,168,83,0.35)" : hov ? "#f0e6cc" : "#d4a853",
        fontFamily: "'Courier Prime', monospace", fontWeight: "bold",
        fontSize: 12, letterSpacing: "0.26em", textTransform: "uppercase",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 0.3s ease",
        opacity: disabled ? 0.6 : 1,
        boxShadow: hov && !disabled
          ? "0 0 28px rgba(212,168,83,0.15), inset 0 0 28px rgba(212,168,83,0.04)"
          : "none",
      }}
    >
      {/* Hover parlama çizgisi — üstten geçiyor */}
      <span style={{
        position: "absolute", top: 0, left: "-100%",
        width: "100%", height: "100%",
        background: "linear-gradient(90deg, transparent, rgba(212,168,83,0.08), transparent)",
        transform: hov ? "translateX(200%)" : "translateX(0)",
        transition: "transform 0.55s ease",
        pointerEvents: "none",
      }} />
      {children}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// AVATAR SEÇİCİ MODALİ
// ═══════════════════════════════════════════════════════════════════════════════
function AvatarModal({ current, onSelect, onClose }) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(null);

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 9000,
        background: "rgba(4,3,1,0.92)",
        backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
      }}
    >
      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        width: "100%", maxWidth: 600, maxHeight: "88vh",
        display: "flex", flexDirection: "column",
        boxShadow: "0 0 80px rgba(0,0,0,0.85)",
        animation: "modalIn 0.3s cubic-bezier(0.16,1,0.3,1)",
      }}>

        {/* Modal başlık */}
        <div style={{
          padding: "20px 26px", borderBottom: `1px solid ${C.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <div>
            <p style={{
              fontFamily: "'Courier Prime', monospace", fontSize: 9,
              letterSpacing: "0.32em", color: C.goldDim,
              textTransform: "uppercase", marginBottom: 4,
            }}>{t('settings.avatar.modalEyebrow')}</p>
            <h3 style={{
              fontFamily: "'Playfair Display', serif", fontSize: 18,
              fontWeight: 700, color: C.gold, margin: 0,
            }}>{t('settings.avatar.modalTitle')}</h3>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 30, height: 30, borderRadius: 6, background: "transparent",
              border: `1px solid ${C.border}`, color: C.goldDim,
              cursor: "pointer", fontSize: 14,
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.2s",
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = C.borderHov; e.currentTarget.style.color = C.gold; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = C.border;    e.currentTarget.style.color = C.goldDim; }}
          >✕</button>
        </div>

        {/* Izgara — 5'li sütun, sabit pixel yükseklik ile kesin kare, binişme yok */}
        <div style={{
          overflowY: "auto", padding: "22px 24px 36px",
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: 12,
        }}>
          {avatarList.map((src, i) => {
            const selected = current === src;
            const hovd     = hovered === i;
            return (
              <div
                key={i}
                onClick={() => onSelect(src)}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  /* Sabit yükseklik: scroll container içinde % çalışmaz, px güvenli */
                  width: "100%",
                  height: 88,
                  border: `2px solid ${
                    selected ? C.gold
                    : hovd    ? "rgba(212,168,83,0.65)"
                    :           "rgba(212,168,83,0.12)"
                  }`,
                  overflow: "hidden", cursor: "pointer", position: "relative",
                  transition: "all 0.22s ease",
                  boxShadow: selected
                    ? `0 0 0 2px rgba(212,168,83,0.35), 0 0 18px ${C.gold}, 0 0 40px rgba(212,168,83,0.3)`
                    : hovd
                    ? `0 0 0 1px rgba(212,168,83,0.25), 0 0 14px rgba(212,168,83,0.45)`
                    : "none",
                  transform: hovd && !selected ? "scale(1.05)" : "scale(1)",
                  background: "rgba(212,168,83,0.06)",
                  flexShrink: 0,
                }}
              >
                <img
                  src={src}
                  alt={`Avatar ${i + 1}`}
                  style={{
                    width: "100%", height: "100%",
                    objectFit: "cover", display: "block",
                  }}
                  onError={e => {
                    e.target.style.display = "none";
                    e.target.parentElement.style.background = "rgba(212,168,83,0.06)";
                  }}
                />
                {selected && (
                  <div style={{
                    position: "absolute", inset: 0,
                    background: "rgba(212,168,83,0.18)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <span style={{ color: C.gold, fontSize: 20, textShadow: `0 0 12px ${C.gold}` }}>✓</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ANA BİLEŞEN
// ═══════════════════════════════════════════════════════════════════════════════
export default function AccountSettings() {
  const { t } = useTranslation();
  const { user, userData, deleteUserAccount } = useAuth();
  const navigate = useNavigate();

  // ── Avatar ──────────────────────────────────────────────────────────────
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  // userData?.photoURL → Realtime DB'deki değer (doğru kaynak)
  // user?.photoURL     → Firebase Auth nesnesi (Google CDN URL'si olabilir — KULLANMA)
  const [selectedAvatar,  setSelectedAvatar]  = useState(userData?.photoURL || "");

  // ── Profil alanları ─────────────────────────────────────────────────────
  const [bio,           setBio]           = useState(userData?.bio         || "");
  const [nationality,   setNationality]   = useState(userData?.nationality || "");
  const [language,      setLanguage]      = useState(userData?.language    || "en");
  const [profileLoading, setProfileLoading] = useState(false);

  // ── Kullanıcı adı ────────────────────────────────────────────────────────
  const [displayName, setDisplayName] = useState(user?.displayName || "");
  const [nameLoading, setNameLoading] = useState(false);
  const [nameMsg,     setNameMsg]     = useState({ type: "", text: "" });

  // ── Şifre sıfırlama ──────────────────────────────────────────────────────
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg,     setPwMsg]     = useState({ type: "", text: "" });

  // ── Hesap silme ──────────────────────────────────────────────────────────
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteMsg,     setDeleteMsg]     = useState({ type: "", text: "" });

  // ── Global toast ─────────────────────────────────────────────────────────
  const [toast, setToast] = useState({ msg: "", type: "success" });

  useEffect(() => {
    const syncLanguageState = (nextLanguage) => {
      const languageCode = nextLanguage.slice(0, 2).toLowerCase();
      if (["tr", "en"].includes(languageCode)) setLanguage(languageCode);
    };

    i18n.on("languageChanged", syncLanguageState);
    return () => i18n.off("languageChanged", syncLanguageState);
  }, []);

  function showToast(msg, type = "success") {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: "", type: "success" }), 3000);
  }

  // userData DB'den güncellenince form alanlarını yansıt
  useEffect(() => {
    if (!userData) return;
    setBio(userData.bio         || "");
    setNationality(userData.nationality || "");
    const lang = userData.language || "en";
    setLanguage(lang);
    // Sayfa yenilendiğinde Firebase'den gelen dil bilgisiyle i18n'i senkronize et
    if (["tr", "en"].includes(lang) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
    // DB'den gelen photoURL'i avatar state'ine yansıt
    if (userData.photoURL) {
      setSelectedAvatar(userData.photoURL);
    }
  }, [userData]);

  // ── Avatar seç → Firebase'e anında yaz ──────────────────────────────────
  async function handleAvatarSelect(src) {
    setSelectedAvatar(src);
    setAvatarModalOpen(false);
    try {
      await updateProfile(auth.currentUser, { photoURL: src });
      await update(ref(db, `users/${user.uid}`), { photoURL: src });
      await syncPublicProfile(auth.currentUser, { ...userData, photoURL: src });
      showToast(t('settings.avatar.updated'));
    } catch {
      showToast(t('settings.avatar.failed'), 'error');
    }
  }

  // ── Profil kaydet (avatar + bio + nationality + language) ─────────────────
  async function handleUpdateProfile() {
    if (bio.trim().length > 200) {
      showToast(t('settings.bio.tooLong'), 'error');
      return;
    }
    setProfileLoading(true);
    try {
      const profileData = {
        ...userData,
        photoURL: selectedAvatar,
        bio: bio.trim(),
        nationality,
      };
      await updateProfile(auth.currentUser, { photoURL: selectedAvatar });
      await update(ref(db, `users/${user.uid}`), {
        photoURL: selectedAvatar,
        bio: bio.trim(),
        nationality,
        language,
      });
      await syncPublicProfile(auth.currentUser, profileData);
      if (["tr", "en"].includes(language)) i18n.changeLanguage(language);
      showToast(t('settings.seal.success'));
    } catch {
      showToast(t('settings.seal.error'), 'error');
    } finally {
      setProfileLoading(false);
    }
  }

  // ── Kullanıcı adı güncelle ────────────────────────────────────────────────
  async function handleUpdateName() {
    const trimmed = displayName.trim();
    if (!trimmed) {
      setNameMsg({ type: 'error', text: t('settings.name.empty') });
      return;
    }
    if (trimmed === user.displayName) {
      setNameMsg({ type: 'error', text: t('settings.name.same') });
      return;
    }
    setNameLoading(true);
    setNameMsg({ type: "", text: "" });
    try {
      const available = await isDisplayNameAvailable(trimmed, user.uid);
      if (!available) {
        setNameMsg({ type: 'error', text: t('settings.name.taken') });
        setNameLoading(false);
        return;
      }

      // Eski key sil (orijinal harf büyüklüğüyle), yeni key yaz
      const oldKey = user.displayName?.trim().replace(/[.#$[\]/]/g, "");
      const newKey = trimmed.replace(/[.#$[\]/]/g, "");
      const { ref: dbRef, remove, set } = await import("firebase/database");
      if (oldKey && oldKey !== newKey) {
        try { await remove(dbRef(db, `usernames/${oldKey}`)); } catch (_) {}
      }
      await set(dbRef(db, `usernames/${newKey}`), { uid: user.uid, displayName: trimmed });
      await updateProfile(auth.currentUser, { displayName: trimmed });
      await update(ref(db, `users/${user.uid}`), { displayName: trimmed });
      await syncPublicProfile(auth.currentUser, { ...userData, displayName: trimmed });

      setNameMsg({ type: 'success', text: t('settings.name.success') });
      showToast(t('settings.name.toastSuccess'));
    } catch {
      setNameMsg({ type: 'error', text: t('settings.name.error') });
    } finally {
      setNameLoading(false);
    }
  }

  // ── Şifre sıfırlama ──────────────────────────────────────────────────────
  async function handlePasswordReset() {
    setPwLoading(true);
    setPwMsg({ type: "", text: "" });
    try {
      await sendPasswordResetEmail(auth, user.email);
      setPwMsg({ type: 'success', text: `${user.email} ${t('settings.password.success')}` });
    } catch {
      setPwMsg({ type: 'error', text: t('settings.password.error') });
    } finally {
      setPwLoading(false);
    }
  }

  // ── Hesap sil ────────────────────────────────────────────────────────────
  async function handleDeleteAccount() {
    const confirmed = window.confirm(
      t('settings.deleteAccount.confirm')
    );
    if (!confirmed) return;
    setDeleteLoading(true);
    setDeleteMsg({ type: "", text: "" });
    try {
      await deleteUserAccount();
      // Hard reload — tüm state temizlenir, kullanıcı login ekranına düşer
      window.location.href = "/";
    } catch (err) {
      setDeleteMsg({
        type: "error",
        text: err.code === "auth/requires-recent-login"
          ? t('settings.deleteAccount.requiresLogin')
          : err.code === "auth/reauth-cancelled"
          ? t('settings.deleteAccount.reauthCancelled')
          : err.code === "auth/wrong-account"
          ? t('settings.deleteAccount.wrongAccount')
          : t('settings.deleteAccount.error'),
      });
    } finally {
      setDeleteLoading(false);
    }
  }

  if (!user) return null;

  return (
    <>
      <style>{`
        @keyframes toastIn {
          from { transform: translateY(14px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes modalIn {
          from { transform: scale(0.97); opacity: 0; }
          to   { transform: scale(1);    opacity: 1; }
        }
        ::-webkit-scrollbar { width: 4px; background: ${C.bg}; }
        ::-webkit-scrollbar-thumb { background: rgba(212,168,83,0.15); }
        ::selection { background: rgba(212,168,83,0.2); color: ${C.ivory}; }
        @media (max-width: 768px) {
          .settings-grid { grid-template-columns: minmax(0, 1fr) !important; }
        }
      `}</style>

      <Toast {...toast} />

      {avatarModalOpen && (
        <AvatarModal
          current={selectedAvatar}
          onSelect={handleAvatarSelect}
          onClose={() => setAvatarModalOpen(false)}
        />
      )}

      <div style={{ minHeight: "100vh", background: C.bg, padding: "72px clamp(20px,6vw,100px) 80px" }}>
        <div style={{ maxWidth: 896, margin: "0 auto" }}>

        {/* ← Geri */}
        <button
          onClick={() => navigate(-1)}
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "6px 12px", borderRadius: 8,
            background: "transparent", border: "1px solid rgba(212,168,83,0.4)",
            color: "var(--bone-text-accent)",
            cursor: "pointer", fontFamily: "'Courier Prime', monospace",
            fontSize: 11, letterSpacing: "0.1em", marginBottom: 32,
            transition: "color 0.2s",
          }}
          onMouseEnter={e => e.currentTarget.style.color = C.gold}
          onMouseLeave={e => e.currentTarget.style.color = "var(--bone-text-accent)"}
        ><span aria-hidden="true">←</span>{t('common.back')}</button>

        {/* Başlık */}
        <div style={{ marginBottom: 40 }}>
          <p style={{
            fontFamily: "'Courier Prime', monospace", fontSize: 9,
            letterSpacing: "0.38em", color: "var(--bone-text-muted)",
            textTransform: "uppercase", marginBottom: 8,
          }}>{t('settings.eyebrow')}</p>
          <h1 style={{
            fontFamily: "'Playfair Display', serif", color: "var(--bone-text-primary)",
            fontSize: 34, fontWeight: 700, margin: 0,
          }}>{t('settings.title')}</h1>
        </div>

        <div className="settings-grid" style={{
          display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 20, alignItems: "start",
        }}>

          {/* ── I · PROFİL ────────────────────────────────────────────────── */}
          <Card style={{ gridColumn: "1 / -1" }}>
            <SectionTitle>{t('settings.section.profile')}</SectionTitle>

            {/* Avatar seçici satırı */}
            <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 24 }}>
              {/* Önizleme */}
              <div style={{
                width: 74, height: 74, flexShrink: 0,
                border: `2px solid ${C.borderHov}`,
                overflow: "hidden", position: "relative",
                boxShadow: selectedAvatar
                  ? `0 0 15px ${C.gold}, 0 0 35px rgba(212,168,83,0.2)`
                  : "none",
                transition: "box-shadow 0.3s",
              }}>
                {selectedAvatar
                  ? <img src={selectedAvatar} alt="Avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : <div style={{
                      width: "100%", height: "100%", background: C.goldFaint,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, color: C.goldDim }}>
                        {(user.displayName || user.email || "?")[0].toUpperCase()}
                      </span>
                    </div>
                }
              </div>

              <div>
                <div style={{
                  fontFamily: "'Playfair Display', serif", fontSize: 15,
                  fontWeight: 700, color: C.ivory, marginBottom: 3,
                }}>{user.displayName || "—"}</div>
                <div style={{
                  fontFamily: "'Courier Prime', monospace", fontSize: 10,
                  color: C.goldDim, marginBottom: 10, letterSpacing: "0.06em",
                }}>{user.email}</div>
                <button
                  onClick={() => setAvatarModalOpen(true)}
                  style={{
                    background: "transparent", border: `1px solid ${C.border}`,
                    color: C.goldDim, padding: "7px 16px",
                    fontFamily: "'Courier Prime', monospace", fontSize: 10,
                    letterSpacing: "0.2em", textTransform: "uppercase",
                    cursor: "pointer", transition: "all 0.2s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = C.borderHov; e.currentTarget.style.color = C.gold; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = C.border;    e.currentTarget.style.color = C.goldDim; }}
                >{t('settings.avatar.change')}</button>
              </div>
            </div>

            <GoldRule />

            {/* Bio */}
            <Label>{t('settings.bio.label')}</Label>
            <TextArea
              value={bio}
              onChange={e => setBio(e.target.value)}
              {...{placeholder: t('settings.bio.placeholder')}}
              maxLength={200}
              rows={4}
            />

            {/* Uyruk */}
            <Label>{t('settings.nationality.label')}</Label>
            <Select
              value={nationality}
              onChange={e => setNationality(e.target.value)}
              options={COUNTRIES}
              {...{placeholder: t('settings.nationality.placeholder')}}
            />

            {/* Dil tercihi */}
            <Label>{t('settings.language.label')}</Label>
            <LangSwitch
              value={language}
              onChange={setLanguage}
              onImmediate={async (lang) => {
                // Anında i18n güncelle
                if (["tr", "en"].includes(lang)) i18n.changeLanguage(lang);
                // Firebase'e de yaz (Mühürle beklemeden)
                try {
                  await update(ref(db, `users/${user.uid}`), { language: lang });
                } catch {
                  // Sessiz — Mühürle'de de yazılacak
                }
              }}
            />

            {/* ── Ayarları Mühürle ── */}
            <div style={{ marginTop: 6, paddingTop: 18, borderTop: `1px solid ${C.border}` }}>
              <SealBtn onClick={handleUpdateProfile} disabled={profileLoading}>
                {profileLoading ? t('settings.seal.loading') : t('settings.seal.button')}
              </SealBtn>
            </div>
          </Card>

          {/* ── II · HESAP BİLGİLERİ ──────────────────────────────────────── */}
          <Card>
            <SectionTitle>{t('settings.section.accountInfo')}</SectionTitle>
            <Label>{t('settings.email.label')}</Label>
            <Input value={user.email} disabled />
          </Card>

          {/* ── III · KULLANICI ADI ───────────────────────────────────────── */}
          <Card>
            <SectionTitle>{t('settings.section.updateName')}</SectionTitle>
            <Label>{t('settings.name.label')}</Label>
            <Input
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              {...{placeholder: t('settings.name.placeholder')}}
              onKeyDown={e => e.key === "Enter" && handleUpdateName()}
            />
            <Msg {...nameMsg} />
            <Btn onClick={handleUpdateName} disabled={nameLoading}>
              {nameLoading ? t('settings.name.checking') : t('settings.name.save')}
            </Btn>
          </Card>

          {/* ── IV · ŞİFRE DEĞİŞTİR ──────────────────────────────────────── */}
          <Card>
            <SectionTitle>{t('settings.section.changePassword')}</SectionTitle>
            <p style={{
              fontFamily: "'Courier Prime', monospace", fontSize: 11,
              color: C.ivoryDim, lineHeight: 1.75, marginBottom: 18,
            }}>
              {t('settings.password.info')}{" "}
              <strong style={{ color: C.gold }}>{user.email}</strong>{" "}
              {t('settings.password.infoSuffix')}
            </p>
            <Msg {...pwMsg} />
            <Btn onClick={handlePasswordReset} disabled={pwLoading} variant="outline">
              {pwLoading ? t('settings.password.sending') : t('settings.password.send')}
            </Btn>
          </Card>

          {/* ── ⚠ TEHLİKELİ ALAN ─────────────────────────────────────────── */}
          <div style={{
            background: C.redDim, border: `1px solid ${C.redBorder}`, borderRadius: 8,
            padding: "28px 32px", marginBottom: 0, gridColumn: "1 / -1",
          }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              marginBottom: 18, paddingBottom: 16,
              borderBottom: `1px solid ${C.redBorder}`,
            }}>
              <span style={{ fontSize: 14 }}>⚠</span>
              <span style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 16, fontWeight: 700, color: C.red,
                letterSpacing: "0.04em",
              }}>{t('settings.section.dangerZone')}</span>
            </div>
            <p style={{
              fontFamily: "'Courier Prime', monospace", fontSize: 11,
              color: "rgba(224,92,92,0.7)", lineHeight: 1.75, marginBottom: 20,
            }}>
              {t('settings.deleteAccount.info')}
            </p>
            <Msg {...deleteMsg} />
            <DangerBtn onClick={handleDeleteAccount} disabled={deleteLoading}>
              {deleteLoading ? t('settings.deleteAccount.deleting') : t('settings.deleteAccount.button')}
            </DangerBtn>
          </div>

        </div>
        </div>
      </div>
    </>
  );
}
